import { useEffect, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { REFERRAL_STATUS_LABELS } from '@/lib/constants'

interface ReferralFiltersProps {
  filters: {
    search: string
    status: string
    source: string
  }
  onFiltersChange: (filters: { search: string; status: string; source: string }) => void
}

const SOURCE_LABELS = {
  all: 'Todas as Plataformas',
  guru: 'Guru',
  yampi: 'Yampi',
} as const

export function ReferralFilters({ filters, onFiltersChange }: ReferralFiltersProps) {
  const [localSearch, setLocalSearch] = useState(filters.search)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== filters.search) {
        onFiltersChange({ ...filters, search: localSearch })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [localSearch, filters, onFiltersChange])

  const hasFilters = filters.search || filters.status !== 'all' || filters.source !== 'all'

  const clearFilters = () => {
    setLocalSearch('')
    onFiltersChange({ search: '', status: 'all', source: 'all' })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row gap-2 w-full">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Nome indicador, indicado, pedido..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="pl-8 bg-card shadow-sm w-full"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Select
            value={filters.status}
            onValueChange={(value) => onFiltersChange({ ...filters, status: value })}
          >
            <SelectTrigger className="w-full sm:w-[180px] bg-card shadow-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              {Object.entries(REFERRAL_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.source}
            onValueChange={(value) => onFiltersChange({ ...filters, source: value })}
          >
            <SelectTrigger className="w-full sm:w-[180px] bg-card shadow-sm">
              <SelectValue placeholder="Plataforma" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearFilters}
            title="Limpar filtros"
            className="hidden sm:flex"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        {hasFilters && (
          <Button
            variant="outline"
            onClick={clearFilters}
            className="w-full sm:hidden flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Limpar filtros
          </Button>
        )}
      </div>
    </div>
  )
}
